/**
 * Mobile swipe carousels for product galleries (static aside + metafield stacks).
 */
(function () {
  const MOBILE_MQ = window.matchMedia('(max-width: 1099px)');

  const STATIC_LIST_SELECTOR =
    '.product__outer--static-column-aside .product__media-list, ' +
    '.product__outer--two-column-aside .product__media-list, ' +
    '.product__outer--static-multicolumns-aside .product__media-list';

  const METAFIELD_SELECTOR = '.product-metafield-gallery .mobile-carousel';

  const SWIPER_ZOOM = {
    maxRatio: 3,
    minRatio: 1,
    toggle: true,
  };

  const wrapSlideForZoom = (slide) => {
    if (slide.querySelector('.swiper-zoom-container')) return;

    const img =
      slide.querySelector('.product__media img') ||
      slide.querySelector('.product__media-item img') ||
      slide.querySelector('.desktop-image') ||
      slide.querySelector('img');

    if (!img || img.closest('video, model-viewer')) return;

    const zoomContainer = document.createElement('div');
    zoomContainer.className = 'swiper-zoom-container';
    img.parentNode.insertBefore(zoomContainer, img);
    zoomContainer.appendChild(img);
  };

  const getVisibleSlideIndex = (swiper, hiddenClass) => {
    if (!swiper?.slides?.length) return 0;
    if (!hiddenClass) return swiper.activeIndex || 0;
    const idx = Array.from(swiper.slides).findIndex(
      (slide) => !slide.classList.contains(hiddenClass)
    );
    return idx >= 0 ? idx : 0;
  };

  const teardownStaticCarousel = (listEl) => {
    if (!listEl || listEl.dataset.mobileCarouselReady !== 'true') return;

    if (listEl.mobileCarouselSwiper) {
      listEl.mobileCarouselSwiper.destroy(true, true);
      listEl.mobileCarouselSwiper = null;
    }

    const wrapper = listEl.querySelector(':scope > .swiper-wrapper');
    if (wrapper) {
      Array.from(wrapper.querySelectorAll('.product__media-item')).forEach((item) => {
        item.classList.remove('swiper-slide');
        listEl.appendChild(item);
      });
      wrapper.remove();
    }

    listEl.closest('.product__media-list-wrapper')
      ?.querySelector('.product__pagination-wrapper--mobile')
      ?.remove();

    listEl.classList.remove('js-media-list', 'swiper', 'product__media-list--mobile-carousel');
    listEl.classList.remove('swiper-initialized');
    delete listEl.dataset.mobileCarouselReady;
  };

  const setupStaticCarousel = (listEl) => {
    if (!listEl || listEl.dataset.mobileCarouselReady === 'true') return;
    if (listEl.classList.contains('swiper-initialized')) return;

    const items = Array.from(listEl.querySelectorAll(':scope > .product__media-item')).filter(
      (item) => !item.classList.contains('product__media-item--hidden')
    );

    if (items.length < 1) return;

    listEl.classList.add('js-media-list', 'swiper', 'product__media-list--mobile-carousel');
    listEl.dataset.direction = 'horizontal';
    listEl.dataset.directionDesc = 'horizontal';

    let swiperWrapper = listEl.querySelector(':scope > .swiper-wrapper');
    if (!swiperWrapper) {
      swiperWrapper = document.createElement('div');
      swiperWrapper.className = 'swiper-wrapper';
      Array.from(listEl.querySelectorAll(':scope > .product__media-item')).forEach((item) => {
        item.classList.add('swiper-slide');
        swiperWrapper.appendChild(item);
      });
      listEl.appendChild(swiperWrapper);
    }

    swiperWrapper.querySelectorAll('.swiper-slide').forEach(wrapSlideForZoom);

    const listWrapper = listEl.closest('.product__media-list-wrapper');
    let paginationWrap = listWrapper?.querySelector('.product__pagination-wrapper--mobile');

    if (!paginationWrap) {
      paginationWrap = document.createElement('div');
      paginationWrap.className =
        'container product__pagination-wrapper product__pagination-wrapper--mobile';
      paginationWrap.innerHTML =
        '<div class="product__pagination product__pagination--mobile swiper-pagination"></div>';
      listEl.insertAdjacentElement('afterend', paginationWrap);
    }

    const paginationEl = paginationWrap.querySelector('.product__pagination--mobile');

    listEl.mobileCarouselSwiper = new Swiper(listEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      autoHeight: true,
      speed: 400,
      resistanceRatio: 0.85,
      watchOverflow: true,
      zoom: SWIPER_ZOOM,
      pagination:
        items.length > 1 && paginationEl
          ? {
              el: paginationEl,
              type: 'bullets',
              clickable: true,
            }
          : undefined,
      on: {
        slideChange() {
          if (typeof window.pauseAllMedia === 'function') {
            window.pauseAllMedia();
          }
        },
      },
    });

    listEl.dataset.mobileCarouselReady = 'true';
  };

  const refreshStaticCarousel = (listEl) => {
    const swiper = listEl?.mobileCarouselSwiper;
    if (!swiper) return;
    swiper.update();
    swiper.slideTo(getVisibleSlideIndex(swiper, 'product__media-item--hidden'), 0);
  };

  const teardownMetafieldCarousel = (carouselEl) => {
    if (!carouselEl) return;

    if (carouselEl.mobileCarouselSwiper) {
      carouselEl.mobileCarouselSwiper.destroy(true, true);
      carouselEl.mobileCarouselSwiper = null;
    }

    carouselEl.classList.remove('swiper-initialized');
    carouselEl.removeAttribute('aria-hidden');
    delete carouselEl.dataset.mobileCarouselReady;
  };

  const setupMetafieldCarousel = (carouselEl) => {
    if (!carouselEl) return;

    const slides = carouselEl.querySelectorAll('.swiper-slide');
    slides.forEach(wrapSlideForZoom);

    if (slides.length <= 1) {
      carouselEl.querySelector('.swiper-pagination')?.classList.add('hidden');
    } else {
      carouselEl.querySelector('.swiper-pagination')?.classList.remove('hidden');
    }

    if (carouselEl.dataset.mobileCarouselReady === 'true' && carouselEl.mobileCarouselSwiper) {
      carouselEl.mobileCarouselSwiper.update();
      carouselEl.mobileCarouselSwiper.slideTo(0, 0);
      return;
    }

    if (carouselEl.classList.contains('swiper-initialized')) return;

    const paginationEl = carouselEl.querySelector('.swiper-pagination');

    carouselEl.mobileCarouselSwiper = new Swiper(carouselEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      autoHeight: true,
      speed: 400,
      resistanceRatio: 0.85,
      watchOverflow: true,
      zoom: SWIPER_ZOOM,
      pagination:
        slides.length > 1 && paginationEl
          ? {
              el: paginationEl,
              type: 'bullets',
              clickable: true,
            }
          : undefined,
    });

    carouselEl.dataset.mobileCarouselReady = 'true';
    carouselEl.removeAttribute('aria-hidden');
  };

  const syncStaticCarousels = () => {
    document.querySelectorAll(STATIC_LIST_SELECTOR).forEach((listEl) => {
      if (MOBILE_MQ.matches) {
        setupStaticCarousel(listEl);
        refreshStaticCarousel(listEl);
      } else {
        teardownStaticCarousel(listEl);
      }
    });
  };

  const syncMetafieldCarousels = () => {
    document.querySelectorAll(METAFIELD_SELECTOR).forEach((carouselEl) => {
      if (MOBILE_MQ.matches) {
        teardownMetafieldCarousel(carouselEl);
        setupMetafieldCarousel(carouselEl);
      } else {
        teardownMetafieldCarousel(carouselEl);
        carouselEl.setAttribute('aria-hidden', 'true');
      }
    });
  };

  const syncAll = () => {
    if (typeof Swiper === 'undefined') return;
    syncStaticCarousels();
    syncMetafieldCarousels();
    if (typeof window.refreshProductGalleryZoom === 'function') {
      window.refreshProductGalleryZoom();
    }
  };

  const onVariantChange = () => {
    window.requestAnimationFrame(syncAll);
  };

  const init = () => {
    if (typeof Swiper === 'undefined') {
      window.setTimeout(init, 50);
      return;
    }
    syncAll();
  };

  if (MOBILE_MQ.addEventListener) {
    MOBILE_MQ.addEventListener('change', syncAll);
  } else {
    MOBILE_MQ.addListener(syncAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('load', syncAll);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('change', (event) => {
    if (event.target.closest('variant-selects, variant-radios, .product-form__input')) {
      onVariantChange();
    }
  });

  window.refreshProductMobileMediaCarousel = syncAll;
})();
